import { useQuery } from "@tanstack/react-query"
import { Eye, FileCheck, Paperclip } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import { aTasaMicro, comoTasa, tasaEntre } from "./conversion"
import { aCentimos, monto } from "./dinero"
import type { Movimiento } from "./tipos"

/**
 * La ficha de un movimiento del extracto: qué es, de dónde sale y qué lo respalda.
 *
 * ⚠ **El soporte de un pago NO cuelga del movimiento: es el comprobante de la ORDEN.**
 * Por eso la fila del extracto de «Pago OP-2026-0003» no traía clip, y para verlo había
 * que ir a Órdenes de pago y buscarla. Aquí se pide el origen —`/transacciones/{id}/
 * origen`— y se enseña el comprobante con su correlativo y en vista rápida, con la
 * orden a un clic.
 *
 * ⚠ **Un «Cambio de Recursos» se explica con su OTRA mitad**: lo que entró en BBVA salió
 * de Binance en otra moneda, y la tasa se deriva de los dos importes.
 */

type OrdenRelacionada = {
  id: number
  codigo: string
  concepto: string
  proveedor: string | null
  estado: string
}

type Origen = {
  tipo: string
  registrado_por: string | null
  cuenta: string
  moneda: string
  orden: OrdenRelacionada | null
  /** Las que financia un cambio de recursos: pueden ser varias (15/10). */
  ordenes?: OrdenRelacionada[]
  comprobante: { orden_id: number; documento_id: number; nombre: string } | null
  contraparte: {
    transaccion_id: number
    cuenta: string
    tipo: string
    monto: string
    moneda: string
    anulado: boolean
  } | null
  tasa_aplicada: string | null
  anulado_por_revertir: boolean
}

const TIPOS: Record<string, { etiqueta: string; tono: Tono }> = {
  PAGO_ORDEN: { etiqueta: "Pago de una orden", tono: "primario" },
  COMISION_ORDEN: { etiqueta: "Comisión de un pago", tono: "neutro" },
  IGTF_ORDEN: { etiqueta: "IGTF de un pago", tono: "neutro" },
  CAMBIO_RECURSOS: { etiqueta: "Cambio de recursos", tono: "info" },
  ABONO_CXP: { etiqueta: "Abono a una cuenta por pagar", tono: "neutro" },
  COBRO_CXC: { etiqueta: "Cobro de una cuenta por cobrar", tono: "neutro" },
  PAGO_RUBRO: { etiqueta: "Pago de un rubro del expediente", tono: "neutro" },
  REINTEGRO_PROVEEDOR: { etiqueta: "Devolución de un proveedor", tono: "info" },
  MANUAL: { etiqueta: "Registrado a mano", tono: "neutro" },
}

export function DetalleMovimiento({
  movimiento,
  onCerrar,
}: {
  movimiento: Movimiento
  onCerrar: () => void
}) {
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const [ordenAbierta, setOrdenAbierta] = useState<number | null>(null)

  const origen = useQuery({
    queryKey: ["movimiento-origen", movimiento.id],
    queryFn: () => api.get<Origen>(`/api/v1/transacciones/${movimiento.id}/origen`),
  })

  // La orden se abre EN LUGAR de la ficha, no encima: dos cajones apilados no se leen.
  if (ordenAbierta !== null) {
    return (
      <FichaOrden ordenId={ordenAbierta} onCerrar={() => setOrdenAbierta(null)} desdeTesoreria />
    )
  }

  const entra = movimiento.tipo === "INGRESO"
  const o = origen.data
  const tipo = o ? (TIPOS[o.tipo] ?? { etiqueta: o.tipo, tono: "neutro" as Tono }) : null

  return (
    <Cajon abierto titulo="Movimiento" onCerrar={onCerrar}>
      <div className="space-y-5 text-sm">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {tipo && <Insignia tono={tipo.tono}>{tipo.etiqueta}</Insignia>}
            {movimiento.anulado && <Insignia tono="peligro">Anulado</Insignia>}
          </div>
          <p className="font-medium">{movimiento.concepto}</p>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <Dato etiqueta={entra ? "Entrada" : "Salida"}>
            <span
              className="font-semibold"
              style={{ color: entra ? "var(--success)" : "var(--destructive)" }}
            >
              {monto(movimiento.monto)} {o?.moneda ?? ""}
            </span>
          </Dato>
          {o && <Dato etiqueta="Cuenta">{o.cuenta}</Dato>}
          <Dato etiqueta="Fecha valor">{comoFecha(movimiento.fecha_valor)}</Dato>
          <Dato etiqueta="Registrado">
            {comoFechaHora(movimiento.creado_en)}
            {o?.registrado_por ? ` · ${o.registrado_por}` : ""}
          </Dato>
          {movimiento.referencia && <Dato etiqueta="Referencia">{movimiento.referencia}</Dato>}
          {o?.tasa_aplicada && (
            <Dato etiqueta="Tasa aplicada">{tasaLegible(o, movimiento.monto)}</Dato>
          )}
          {movimiento.anulado && movimiento.motivo_anulacion && (
            <Dato etiqueta="Motivo de la anulación">{movimiento.motivo_anulacion}</Dato>
          )}
        </dl>

        {origen.isPending && <p style={{ color: "var(--fg-muted)" }}>Buscando de dónde sale…</p>}
        {origen.isError && (
          <p role="alert" style={{ color: "var(--destructive)" }}>
            No se pudo saber de dónde sale este movimiento.
          </p>
        )}

        {o?.orden && (
          <section className="tarjeta space-y-2 p-3">
            <h3 className="text-xs font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
              {o.tipo === "CAMBIO_RECURSOS"
                ? (o.ordenes?.length ?? 0) > 1
                  ? `Financia ${o.ordenes?.length} órdenes`
                  : "Financia la orden"
                : "Orden de pago"}
            </h3>
            {/* Un cambio de recursos puede pagar varias; un pago, sólo la suya. */}
            {(o.tipo === "CAMBIO_RECURSOS" && o.ordenes?.length ? o.ordenes : [o.orden]).map(
              (orden) => (
                <div key={orden.id} className="space-y-2">
                  <p>
                    <strong className="tabular-nums">{orden.codigo}</strong> ·{" "}
                    {orden.proveedor ?? "—"}
                  </p>
                  <p style={{ color: "var(--fg-muted)" }}>{orden.concepto}</p>
                  <Boton tamano="sm" onClick={() => setOrdenAbierta(orden.id)}>
                    <FileCheck size={14} />{" "}
                    {(o.ordenes?.length ?? 0) > 1 ? `Abrir ${orden.codigo}` : "Abrir la orden"}
                  </Boton>
                </div>
              ),
            )}
            {o.anulado_por_revertir && (
              <p style={{ color: "var(--fg-muted)" }}>Este pago se revirtió.</p>
            )}
          </section>
        )}

        {o?.contraparte && (
          <section className="tarjeta space-y-1 p-3">
            <h3 className="text-xs font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
              La otra mitad del cambio
            </h3>
            <p>
              {o.contraparte.tipo === "INGRESO" ? "Entraron" : "Salieron"}{" "}
              <strong className="tabular-nums">
                {monto(o.contraparte.monto)} {o.contraparte.moneda}
              </strong>{" "}
              {o.contraparte.tipo === "INGRESO" ? "en" : "de"} {o.contraparte.cuenta}
              {o.contraparte.anulado ? " (anulado)" : ""}.
            </p>
          </section>
        )}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Soporte</h3>
          <ul className="space-y-1">
            {o?.comprobante && (
              <li>
                <Archivo
                  etiqueta="Comprobante del pago"
                  nombre={o.comprobante.nombre}
                  onVer={() =>
                    setViendo({
                      ruta: `/api/v1/ordenes-pago/${o.comprobante?.orden_id}/documentos/${o.comprobante?.documento_id}/contenido`,
                      nombre: o.comprobante?.nombre ?? "",
                    })
                  }
                />
              </li>
            )}
            {movimiento.soportes.map((s) => (
              <li key={s.id}>
                <Archivo
                  etiqueta="Soporte"
                  nombre={s.nombre_original}
                  onVer={() =>
                    setViendo({
                      ruta: `/api/v1/transacciones/${movimiento.id}/soportes/${s.id}/contenido`,
                      nombre: s.nombre_original,
                    })
                  }
                />
              </li>
            ))}
          </ul>
          {!o?.comprobante && movimiento.soportes.length === 0 && (
            <p style={{ color: "var(--fg-muted)" }}>
              {o?.tipo === "CAMBIO_RECURSOS"
                ? "Sin soporte: en un cambio de recursos lo respalda la otra mitad, que ya está en el sistema."
                : "Este movimiento no tiene soporte."}
            </p>
          )}
        </section>
      </div>
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </Cajon>
  )
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: "var(--fg-muted)" }}>{etiqueta}</dt>
      <dd className="tabular-nums">{children}</dd>
    </>
  )
}

function Archivo({
  etiqueta,
  nombre,
  onVer,
}: {
  etiqueta: string
  nombre: string
  onVer: () => void
}) {
  return (
    <button
      type="button"
      onClick={onVer}
      className="foco-cristal flex min-w-0 items-center gap-2 text-left underline"
    >
      <Paperclip size={14} className="shrink-0" />
      <span className="shrink-0" style={{ color: "var(--fg-muted)" }}>
        {etiqueta}:
      </span>
      <span className="truncate tabular-nums">{nombre}</span>
      <Eye size={14} className="shrink-0" aria-hidden={true} />
    </button>
  )
}

/**
 * La tasa como se habla: «1.007,05 BS por 1 USDT», nunca «0,000993» (15/10).
 *
 * ⚠ El servidor la guarda como «unidades del destino por 1 del origen», y en un cambio
 * de bolívares a USDT eso da un número menor que uno que nadie lee. Con la otra mitad
 * a la vista se saca de los DOS importes —los de los extractos— con la moneda que más
 * vale como «por 1», a cuatro decimales. Sin ella, si es menor que uno se enseña invertida.
 */
function tasaLegible(o: Origen, montoPropio: string): string {
  const c = o.contraparte
  if (c) {
    const propio = aCentimos(montoPropio)
    const otro = aCentimos(c.monto)
    const [arriba, abajo, monedaArriba, monedaAbajo] =
      propio >= otro ? [propio, otro, o.moneda, c.moneda] : [otro, propio, c.moneda, o.moneda]
    const tasa = tasaEntre(arriba, abajo)
    if (tasa !== null) return `${comoTasa(a4(tasa))} ${monedaArriba} por 1 ${monedaAbajo}`
  }
  const micro = aTasaMicro(o.tasa_aplicada ?? "")
  if (micro === null) return o.tasa_aplicada ?? ""
  return micro >= 1_000_000n
    ? comoTasa(micro)
    : comoTasa(a4((1_000_000n * 1_000_000n + micro / 2n) / micro))
}

/** A cuatro decimales, que es como se lee una tasa: los seis son para calcular. */
function a4(micro: bigint): bigint {
  return ((micro + 50n) / 100n) * 100n
}
